import React, { useEffect, useState } from 'react';
import { Bell, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { usePushNotifications } from '../../hooks/usePushNotifications';

const DISMISS_KEY = 'bc_push_prompt_dismissed_at';
const DISMISS_DAYS = 7;

// Invite logged-in contractors to enable push alerts for new jobs (once per week if dismissed)
export const PushPromptBanner = () => {
  const { user } = useAuth();
  const { isSupported, isSubscribed, permission, subscribe } = usePushNotifications();
  const [hidden, setHidden] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const recentlyDismissed = at && Date.now() - at < DISMISS_DAYS * 86400000;
    const isContractor = user && ['contractor', 'both'].includes(user.user_type);
    const timer = setTimeout(() => {
      setHidden(!(isContractor && isSupported && !isSubscribed && permission === 'default' && !recentlyDismissed));
    }, 2500);
    return () => clearTimeout(timer);
  }, [user, isSupported, isSubscribed, permission]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
  };

  const enable = async () => {
    setBusy(true);
    const ok = await subscribe();
    setBusy(false);
    if (ok) {
      toast.success('Notifications activées : vous serez prévenu de chaque nouveau chantier');
      setHidden(true);
    } else {
      toast.error("Impossible d'activer les notifications sur cet appareil");
      dismiss();
    }
  };

  if (hidden) return null;

  return (
    <div className="fixed bottom-20 md:bottom-6 inset-x-3 md:inset-x-auto md:left-6 md:w-[380px] z-[60]" data-testid="push-prompt-banner">
      <div className="rounded-xl bg-white border border-[#1E3A8A]/15 shadow-2xl p-4 flex gap-3 items-start">
        <span className="w-10 h-10 rounded-full bg-[#F59E0B]/15 text-[#F59E0B] flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-sm text-[#111827]">Soyez prévenu des nouveaux chantiers</p>
          <p className="text-xs text-[#4B5563] mt-1">Recevez une notification sur votre téléphone dès qu'un chantier est publié.</p>
          <Button size="sm" className="mt-2 bg-[#1E3A8A] hover:bg-[#172e6e] text-white h-8" onClick={enable} disabled={busy} data-testid="push-prompt-enable">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bell className="w-4 h-4 mr-1" />} Activer les notifications
          </Button>
        </div>
        <button onClick={dismiss} className="p-1 rounded hover:bg-black/5" aria-label="Fermer" data-testid="push-prompt-close">
          <X className="w-4 h-4 text-[#6B7280]" />
        </button>
      </div>
    </div>
  );
};
